import { useCallback, useEffect, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { DocumentType } from "@api/graphql/generated/generated-types";
import { useCourieStore } from "@src/common/lib/store";
import { showErrorToast } from "@src/common/lib/NetworkErrorHandling";
import {
  GET_COMPLIANCE_POLICIES,
  GetCompliancePoliciesData,
  GetCompliancePoliciesVars,
} from "../graphql/queries";

export interface CascadeResult {
  required: DocumentType[];
  warned: DocumentType[];
}

/**
 * Resolves the credential cascade: courier -> end customer -> locations,
 * then folds package-type policies on additively.
 *
 * Each sequential layer is applied in priority order (excluded, then warned, then required)
 * so higher-priority categories always win:
 * 1. excludedDocumentTypes — removes from both required and warned
 * 2. warnedDocumentTypes — downgrades to soft-block (moves out of required into warned)
 * 3. requiredDocumentTypes — promotes to hard-block (moves out of warned into required)
 *
 * For same-level conflicts (multiple locations or package types) the types are merged into a
 * single set per category before applying, so the same priority ordering
 * resolves cross-location conflicts naturally.
 *
 * Package types are not a sequential layer: required always promotes; recommended
 * (warned) only applies when that document type is not already required. Package
 * types do not contribute excluded.
 */
export function resolveCascade(
  courierId: string,
  endCustomerId: string | undefined,
  locationIds: string[],
  policies: GetCompliancePoliciesData["driverCompliancePolicies"],
  packageTypeIds: string[] = [],
): CascadeResult {
  const policyMap = new Map(policies.map((p) => [p.entityId, p]));

  const required = new Set<DocumentType>();
  const warned = new Set<DocumentType>();

  function applyLayer(
    excluded: DocumentType[],
    warn: DocumentType[],
    req: DocumentType[],
  ) {
    for (const type of excluded) {
      required.delete(type);
      warned.delete(type);
    }
    for (const type of warn) {
      required.delete(type);
      warned.add(type);
    }
    for (const type of req) {
      warned.delete(type);
      required.add(type);
    }
  }

  function applyPolicy(entityId: string) {
    const policy = policyMap.get(entityId);
    if (!policy) return;
    applyLayer(
      policy.excludedDocumentTypes ?? [],
      policy.warnedDocumentTypes ?? [],
      policy.requiredDocumentTypes ?? [],
    );
  }

  function applyLocationPolicies(ids: string[]) {
    const allRequired = new Set<DocumentType>();
    const allWarned = new Set<DocumentType>();
    const allExcluded = new Set<DocumentType>();

    for (const id of ids) {
      const policy = policyMap.get(id);
      if (!policy) continue;
      for (const type of policy.requiredDocumentTypes ?? []) {
        allRequired.add(type);
      }
      for (const type of policy.warnedDocumentTypes ?? []) {
        allWarned.add(type);
      }
      for (const type of policy.excludedDocumentTypes ?? []) {
        allExcluded.add(type);
      }
    }

    // Resolve same-level conflicts: Required > Warned > Excluded
    applyLayer(
      Array.from(allExcluded),
      Array.from(allWarned),
      Array.from(allRequired),
    );
  }

  function applyPackageTypePolicies(ids: string[]) {
    const allRequired = new Set<DocumentType>();
    const allWarned = new Set<DocumentType>();

    for (const id of ids) {
      const policy = policyMap.get(id);
      if (!policy) continue;
      for (const type of policy.requiredDocumentTypes ?? []) {
        allRequired.add(type);
      }
      for (const type of policy.warnedDocumentTypes ?? []) {
        allWarned.add(type);
      }
    }

    for (const type of Array.from(allWarned)) {
      if (!required.has(type) && !allRequired.has(type)) {
        warned.add(type);
      }
    }
    for (const type of Array.from(allRequired)) {
      warned.delete(type);
      required.add(type);
    }
  }

  applyPolicy(courierId);
  if (endCustomerId) applyPolicy(endCustomerId);
  if (locationIds.length > 0) applyLocationPolicies(locationIds);
  if (packageTypeIds.length > 0) applyPackageTypePolicies(packageTypeIds);

  return {
    required: Array.from(required),
    warned: Array.from(warned),
  };
}

export function useAutoFillDocumentTypes(
  courierId: string | undefined,
  endCustomerId: string | undefined,
  locationIds: string[],
  packageTypeIds: string[] = [],
) {
  const { showToast } = useCourieStore();
  const [result, setResult] = useState<CascadeResult>({
    required: [],
    warned: [],
  });

  const paramsRef = useRef({
    courierId,
    endCustomerId,
    locationIds,
    packageTypeIds,
  });
  paramsRef.current = { courierId, endCustomerId, locationIds, packageTypeIds };
  const requestSeq = useRef(0);

  const [fetchPolicies] = useLazyQuery<
    GetCompliancePoliciesData,
    GetCompliancePoliciesVars
  >(GET_COMPLIANCE_POLICIES, { fetchPolicy: "network-only" });

  const refetch = useCallback(() => {
    const {
      courierId: cId,
      endCustomerId: ecId,
      locationIds: lIds,
      packageTypeIds: ptIds,
    } = paramsRef.current;
    if (!cId) {
      requestSeq.current += 1;
      return;
    }
    const entityIds = [cId];
    if (ecId) entityIds.push(ecId);
    entityIds.push(...lIds.filter(Boolean));
    entityIds.push(...ptIds.filter(Boolean));

    const seq = ++requestSeq.current;
    fetchPolicies({ variables: { courierId: cId, entityIds } }).then((res) => {
      if (seq !== requestSeq.current) return;
      const resolved = resolveCascade(
        cId,
        ecId,
        lIds,
        res.data?.driverCompliancePolicies ?? [],
        ptIds,
      );
      setResult(resolved);
    }).catch((err) => {
      if (seq !== requestSeq.current) return;
      showErrorToast(err, showToast);
    });
  }, [fetchPolicies]);

  useEffect(() => {
    refetch();
  }, [
    courierId,
    endCustomerId,
    locationIds.join(","),
    packageTypeIds.join(","),
    refetch,
  ]);

  return result;
}
